// ============================================================================
// Typed view of the trainer BUSINESS / INCOME report payload.
// ----------------------------------------------------------------------------
// The shared API type `BusinessReport` is intentionally loose (Record<string,
// unknown>) — the backend serializes nested engine Results. This file narrows
// that JSON into precise, locally-owned types that mirror EXACTLY what
// `viccofitness/reports.py::business_report_data` produces (verified against the
// serialized payload).
//
// Optional-first: every section carries an `ok` flag for conditional rendering,
// so the page renders only sections with data. `income` is `{ ok: false }`
// whenever no delivered session ever carried a fee — the whole income section
// is then omitted.
//
// Income is ALWAYS per-currency: `income.by_currency` is a list of blocks, one
// per currency seen in paid sessions. There is no top-level income total or
// currency code. TrendsSection mirrors this with `income_by_currency` (keyed by
// currency code).
//
// We keep these types here (not in src/api/types.ts) to honour the ownership
// boundary: this page owns its own folder. `parseReport()` does the one cast
// from the loose API shape to this typed shape at the edge.
// ============================================================================
import type { BusinessReport, SeriesPoint } from "../../api";

export type ReportPeriod = "all" | "month" | "quarter" | "year";

/** Period values offered in the selector, in display order. */
export const REPORT_PERIODS: ReportPeriod[] = [
  "all",
  "month",
  "quarter",
  "year",
];

export function isReportPeriod(v: string): v is ReportPeriod {
  return (REPORT_PERIODS as string[]).includes(v);
}

/** A {client_id, client_name, sessions} row in the sessions breakdown. */
export interface SessionsPerClient {
  client_id: number;
  client_name: string | null;
  sessions: number;
}

/** A {client_id, client_name, hours} row in the hours breakdown. */
export interface HoursPerClient {
  client_id: number;
  client_name: string | null;
  hours: number;
}

/** A {client_id, client_name, income} row in the income breakdown. */
export interface IncomePerClient {
  client_id: number;
  client_name: string | null;
  income: number;
}

export interface SessionsSection {
  ok: boolean;
  total: number;
  per_client: SessionsPerClient[];
}

export interface HoursSection {
  ok: boolean;
  total: number;
  per_client: HoursPerClient[];
}

/** One currency's income metrics inside the income section. */
export interface CurrencyBlock {
  currency: string;
  /** Pre-resolved display symbol (e.g. "HK$" / "US$" / "PS"). */
  currency_symbol: string;
  total: number;
  paid: number;
  outstanding: number;
  avg_per_session: number | null;
  avg_per_hour: number | null;
  per_client: IncomePerClient[];
  /** Fee excluded from income due to no-shows in this currency. */
  no_show_excluded: number;
  /** Count of no-show sessions excluded in this currency. */
  no_show_count_excluded: number;
}

/** Present only when at least one delivered session carried a fee. */
export interface IncomeSectionOk {
  ok: true;
  /** Total no-show count across ALL currencies (headline; see per-block for fee). */
  no_show_count: number;
  by_currency: CurrencyBlock[];
}

export interface IncomeSectionEmpty {
  ok: false;
  /** No-shows are still reported even when no delivered session carried a fee. */
  no_show_count: number;
}

export type IncomeSection = IncomeSectionOk | IncomeSectionEmpty;

/** Month-bucketed (YYYY-MM) trend point lists, oldest month first. */
export interface TrendsSection {
  sessions: SeriesPoint[];
  hours: SeriesPoint[];
  /** One series per currency code, e.g. { "HKD": [{x,y}…], "USD": [{x,y}…] }. */
  income_by_currency: Record<string, SeriesPoint[]>;
}

/** The fully-typed business report. */
export interface TypedBusinessReport {
  period: ReportPeriod;
  sessions: SessionsSection;
  hours: HoursSection;
  income: IncomeSection;
  trends: TrendsSection;
}

/**
 * Narrow the loose API payload to the typed report at the page boundary.
 * The backend guarantees this shape; the single cast is contained here.
 */
export function parseReport(raw: BusinessReport): TypedBusinessReport {
  return raw as unknown as TypedBusinessReport;
}

/** True when at least one section has something worth rendering. */
export function hasAnyData(r: TypedBusinessReport): boolean {
  return (
    r.sessions.ok ||
    r.hours.ok ||
    r.income.ok ||
    r.trends.sessions.length > 0
  );
}

/** Type guard: the income section is present (had at least one fee). */
export function hasIncome(income: IncomeSection): income is IncomeSectionOk {
  return income.ok && (income as IncomeSectionOk).by_currency.length > 0;
}
